import { useRef } from 'react'
import { Check, Star } from 'lucide-react'
import AiRatingStars from './AiRatingStars'
import { RATING_COLORS } from '@/lib/rating-colors'
import { sourceBadgeLabel } from '@/lib/source-badge'
import { LUCK_LABEL, luckGain, statusGainEntries } from '@/lib/status-gain'
import { Badge } from '@/components/ui/badge'
import type { Achievement } from '@/types'

type Props = {
  achievement: Achievement
  onClick?: () => void
  isSelecting?: boolean
  isSelected?: boolean
  onSelect?: (id: number) => void
  onLongPress?: () => void
}

const MAX_VISIBLE_TAGS = 4
const MAX_VISIBLE_STATUS = 3


export default function AchievementCard({ achievement, onClick, isSelecting, isSelected, onSelect, onLongPress }: Props) {
  const allTags = [
    ...achievement.tags.map(t => ({ label: t, isAi: false })),
    ...achievement.ai_tags.map(t => ({ label: t, isAi: true })),
  ]
  const visibleTags = allTags.slice(0, MAX_VISIBLE_TAGS)
  const extraTagCount = allTags.length - MAX_VISIBLE_TAGS
  const sourceLabel = sourceBadgeLabel(achievement.source)
  const allStatusEntries = statusGainEntries(achievement.status_gain)
  const visibleStatusEntries = allStatusEntries.slice(0, MAX_VISIBLE_STATUS)
  const extraStatusCount = allStatusEntries.length - MAX_VISIBLE_STATUS
  const luck = luckGain(achievement.status_gain)
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const handlePointerDown = () => {
    if (!isSelecting && onLongPress) {
      longPressTimer.current = setTimeout(() => {
        onLongPress()
        onSelect?.(achievement.id)
      }, 500)
    }
  }
  const handlePointerUp = () => {
    if (longPressTimer.current) { clearTimeout(longPressTimer.current); longPressTimer.current = null }
  }

  return (
    <div
      className={`px-4 py-3 rounded-lg border cursor-pointer transition-colors select-none
        ${isSelected ? 'bg-primary/10 border-primary' : 'bg-card active:bg-muted'}
      `}
      onClick={() => {
        if (isSelecting) { onSelect?.(achievement.id); return }
        onClick?.()
      }}
      onPointerDown={handlePointerDown}
      onPointerUp={handlePointerUp}
      onPointerLeave={handlePointerUp}
      onPointerCancel={handlePointerUp}
    >
      {/* 1行目：タイトル + 日付 */}
      <div className="flex items-center gap-2">
        {isSelecting && (
          <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 ${
            isSelected ? 'bg-primary border-primary' : 'border-muted-foreground'
          }`}>
            {isSelected && <Check size={12} className="text-primary-foreground" />}
          </div>
        )}
        <p className="font-medium truncate flex-1">{achievement.title}</p>
        {sourceLabel && (
          <Badge variant="outline" className="text-[10px] px-1.5 py-0 shrink-0 text-muted-foreground">
            {sourceLabel}
          </Badge>
        )}
        <span className="text-xs text-muted-foreground shrink-0">
          {new Date(achievement.achieved_at).toLocaleDateString('ja-JP')}
        </span>
      </div>

      {/* 2行目：タグ（ユーザー＋AI統合）+ 自己評価星 */}
      <div className="flex items-center gap-1.5 mt-1.5 overflow-hidden">
        <div className="flex items-center gap-1.5 overflow-hidden flex-1">
          {visibleTags.map((tag) => (
            tag.isAi
              ? <Badge key={`ai:${tag.label}`} className="text-xs px-1.5 py-0 shrink-0 bg-green-500/10 text-green-700 dark:text-green-400 border-0">{tag.label}</Badge>
              : <Badge key={`user:${tag.label}`} variant="secondary" className="text-xs px-1.5 py-0 shrink-0">{tag.label}</Badge>
          ))}
          {extraTagCount > 0 && (
            <Badge variant="outline" className="text-xs px-1.5 py-0 shrink-0">+{extraTagCount}</Badge>
          )}
        </div>
        <div className="flex gap-0.5 shrink-0">
          {Array.from({ length: 5 }).map((_, i) => (
            <Star key={i} size={12}
              className={i < achievement.rating ? RATING_COLORS.self : RATING_COLORS.empty}
            />
          ))}
        </div>
      </div>

      {/* 3行目：ステータスゲイン + AI rate（AI 分析済みかつ表示内容があるとき） */}
      {achievement.is_ai_analyzed && (visibleStatusEntries.length > 0 || luck !== 0 || achievement.ai_rating !== null) && (
        <div className="flex items-center gap-1.5 mt-1 overflow-hidden">
          <div className="flex items-center gap-1.5 overflow-hidden flex-1">
            {visibleStatusEntries.map(({ key, label, val }) => (
              <span key={key} className="text-xs px-2 py-0.5 rounded-full shrink-0 bg-blue-500/10 text-blue-700 dark:text-blue-400">
                {label} +{val}
              </span>
            ))}
            {luck !== 0 && (
              <span className={`text-xs px-2 py-0.5 rounded-full shrink-0 ${
                luck > 0
                  ? 'bg-green-500/10 text-green-700 dark:text-green-400'
                  : 'bg-red-500/10 text-red-700 dark:text-red-400'
              }`}>
                {LUCK_LABEL} {luck > 0 ? `+${luck}` : luck}
              </span>
            )}
            {extraStatusCount > 0 && (
              <Badge variant="outline" className="text-xs px-1.5 py-0 shrink-0">+{extraStatusCount}</Badge>
            )}
          </div>
          {achievement.ai_rating !== null && (
            <div className="shrink-0">
              <AiRatingStars rating={achievement.ai_rating} size={12} />
            </div>
          )}
        </div>
      )}
    </div>
  )
}
